import React, { useState, useEffect } from 'react';
import { Form, Button, Space, message, Input, Upload, Radio, Divider, Row, Col, Select, Card, Collapse, Typography } from 'antd';
import { UploadOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getParametersForImagingStudy } from '../utils/imagingParameters';
import MomentDatePicker from './MomentDatePicker';
import ImagingLanguageToggle from './ImagingLanguageToggle';
import { useImagingLanguage } from '../hooks/useImagingLanguage';

const { TextArea } = Input;
const { Panel } = Collapse;
const { Title } = Typography;

const AbdominalUSStudyInputForm = ({ patientId, onSave, onCancel, initialData }) => {
  const [form] = Form.useForm();
  const [allParameters, setAllParameters] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [documentType, setDocumentType] = useState('file');
  const [externalLink, setExternalLink] = useState('');
  const [existingDocument, setExistingDocument] = useState(null);

  // Language management hook
  const {
    changeLanguage,
    getTranslatedParameterName,
    getTranslatedOption,
    getTranslatedPlaceholder,
    isVietnamese
  } = useImagingLanguage();

  useEffect(() => {
    // Get parameters from all 5 organ systems plus the date parameter
    const abdominalUSParams = getParametersForImagingStudy('Abdominal US');
    const liverUSParams = getParametersForImagingStudy('Liver US');
    const gallbladderUSParams = getParametersForImagingStudy('Gallbladder US');
    const spleenUSParams = getParametersForImagingStudy('Spleen US');
    const kidneyUSParams = getParametersForImagingStudy('Kidney US');
    const bladderUSParams = getParametersForImagingStudy('Bladder US');

    // Combine all parameters into one comprehensive list
    const combinedParameters = [
      ...abdominalUSParams, // Date parameter
      ...liverUSParams,
      ...gallbladderUSParams,
      ...spleenUSParams,
      ...kidneyUSParams,
      ...bladderUSParams
    ];

    setAllParameters(combinedParameters);
  }, []);

  // Handle initial data population for edit mode
  useEffect(() => {
    if (initialData) {
      console.log('AbdominalUS Initial Data:', initialData);
      
      // Populate form fields from flowsheet data
      if (initialData.details && initialData.details.length > 0) {
        console.log('AbdominalUS: Populating form with initial data:', initialData.details);

        const formValues = {};

        // Process all fields (no special date handling needed)
        initialData.details.forEach(detail => {
          if (detail.name && detail.value !== undefined) {
            // Skip any date fields completely
            if (!detail.key.includes('_date')) {
              formValues[detail.key] = detail.value;
            }
          }
        });
        console.log('AbdominalUS: Final form values to set:', formValues);
        form.setFieldsValue(formValues);
      }

      // Handle existing document
      if (initialData.document) {
        setExistingDocument(initialData.document);
        if (initialData.document.externalLink) {
          setDocumentType('link');
          setExternalLink(initialData.document.externalLink);
        } else if (initialData.document.fileName) {
          setDocumentType('file');
          setUploadedFile({
            name: initialData.document.fileName,
            status: 'done',
            url: `/api/v1/documents/file/${initialData.document._id}`
          });
        }
      }
    }
  }, [initialData, form, allParameters]);

  const handleFinish = (values) => {
    // Filter out any parameters that were not filled
    const filledValues = {};
    for (const key in values) {
      if (values[key] !== undefined && values[key] !== null) {
        if (typeof values[key] === 'string' && values[key].trim() === '') {
          continue; // Skip empty strings
        }
        filledValues[key] = values[key];
      }
    }

    // Check if we have either file/link or form data
    const hasFile = fileList.length > 0;
    const hasLink = documentType === 'link' && externalLink.trim() !== '';
    const hasFindings = Object.keys(filledValues).length > 0;

    if (!hasFindings && !hasFile && !hasLink) {
      message.warning('Please upload an Abdominal US file, provide an external link, or enter at least one finding.');
      return;
    }

    // Validate external link if provided
    if (documentType === 'link' && externalLink.trim() !== '') {
      try {
        new URL(externalLink.trim());
      } catch (error) {
        message.error('Please enter a valid URL for the external link.');
        return;
      }
    }

    // Process all findings (no date field to separate)
    const findingsArray = Object.entries(filledValues).map(([key, value]) => {
      const paramDefinition = allParameters.find(p => p.key === key);
      let processedValue = value;

      // Handle date values - convert moment objects to ISO strings
      if (paramDefinition && paramDefinition.inputType === 'date' && value && typeof value === 'object' && value.format) {
        processedValue = value.format('YYYY-MM-DD');
      }

      return {
        key: key,
        name: paramDefinition ? paramDefinition.name : key,
        value: processedValue,
      };
    });

    // Include document information
    const submissionData = {
      findings: findingsArray,
      studyType: 'Abdominal US',
      documentType: documentType,
      file: hasFile ? fileList[0] : null,
      externalLink: hasLink ? externalLink.trim() : null,
      existingDocumentId: existingDocument?._id || null
    };

    console.log('Submitting Abdominal US data:', submissionData);
    onSave(submissionData);
  };

  const handleDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
    if (e.target.value === 'file') {
      setExternalLink('');
    } else {
      setFileList([]);
      setUploadedFile(null);
    }
  };

  const handlePreview = () => {
    if (uploadedFile && uploadedFile.url) {
      window.open(uploadedFile.url, '_blank');
    }
  };

  const uploadProps = {
    name: 'abdominalUSFile',
    multiple: false,
    fileList: fileList,
    beforeUpload: (file) => {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/dicom'];
      const isDicom = file.name.toLowerCase().endsWith('.dcm');
      
      if (!allowedTypes.includes(file.type) && !isDicom) {
        message.error('Please upload a valid file format (PDF, JPEG, PNG, or DICOM).');
        return false;
      }

      // Validate file size (10MB limit)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('File must be smaller than 10MB.');
        return false;
      }

      setFileList([file]);
      setUploadedFile(file);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([]);
      setUploadedFile(null);
    },
    showUploadList: false
  };

  // Helper function to group parameters by organ system (excluding date)
  const getParametersByOrgan = () => {
    const organGroups = {
      'Liver': allParameters.filter(p => p.key.includes('liver_us_') && !p.key.includes('gallbladder_us_')),
      'Gallbladder': allParameters.filter(p => p.key.includes('gallbladder_us_')),
      'Spleen': allParameters.filter(p => p.key.includes('spleen_us_') && !p.key.includes('gallbladder_us_')),
      'Kidneys': allParameters.filter(p => p.key.includes('kidney_us_') && !p.key.includes('gallbladder_us_')),
      'Bladder': allParameters.filter(p => p.key.includes('bladder_us_') && !p.key.includes('gallbladder_us_'))
    };
    return organGroups;
  };



  const renderParameterInput = (param) => {
    if (param.inputType === 'radio') {
      return (
        <Radio.Group optionType="button" buttonStyle="solid">
          {param.options && param.options.map(option => (
            <Radio.Button key={option} value={option} style={{ marginBottom: 4 }}>
              {getTranslatedOption(option)}
            </Radio.Button>
          ))}
        </Radio.Group>
      );
    } else if (param.inputType === 'select') {
      return (
        <Select placeholder={isVietnamese ? `Chọn ${getTranslatedParameterName(param.key)}` : `Select ${param.name}`} allowClear>
          {param.options && param.options.map(option => (
            <Select.Option key={option} value={option}>
              {getTranslatedOption(option)}
            </Select.Option>
          ))}
        </Select>
      );
    } else if (param.inputType === 'date') {
      return (
        <MomentDatePicker
          style={{ width: '100%' }}
          placeholder={isVietnamese ? `Chọn ${getTranslatedParameterName(param.key)}` : `Select ${param.name}`}
          format="YYYY-MM-DD"
        />
      );
    } else if (param.inputType === 'textarea') {
      return (
        <TextArea
          rows={3}
          placeholder={getTranslatedPlaceholder(param.key)}
        />
      );
    } else {
      return (
        <Input placeholder={getTranslatedPlaceholder(param.key)} />
      );
    }
  };

  const organGroups = getParametersByOrgan();

  return (
    <div style={{ padding: '0 24px' }}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            {isVietnamese ? 'Nhập dữ liệu Siêu âm Bụng' : 'Abdominal US Study Entry'}
          </Title>
          <ImagingLanguageToggle onLanguageChange={changeLanguage} />
        </div>

        <Row gutter={24}>
          {/* Left Column - Document Upload/Link */}
          <Col xs={24} lg={8}>
            <h4>{isVietnamese ? 'Tài liệu Siêu âm Bụng' : 'Abdominal US Document'}</h4>
            
            {existingDocument && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
                <strong>Current Document:</strong> {existingDocument.documentName}
                <br />
                <small>
                  {existingDocument.externalLink ? 'External Link' : `File: ${existingDocument.fileName}`}
                  {existingDocument.createdAt && ` • ${new Date(existingDocument.createdAt).toLocaleDateString()}`}
                </small>
              </div>
            )}

            <Form.Item label="Document Type">
              <Radio.Group value={documentType} onChange={handleDocumentTypeChange}>
                <Radio.Button value="file">Upload File</Radio.Button>
                <Radio.Button value="link">External Link</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {documentType === 'file' ? (
              <Form.Item
                label="Upload Abdominal US File"
                help="Supported formats: PDF, JPEG, PNG, DICOM (.dcm). Maximum size: 10MB"
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>
                    Select Abdominal US File
                  </Button>
                </Upload>
                {uploadedFile && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={handlePreview}
                      size="small"
                    >
                      Preview {uploadedFile.name}
                    </Button>
                  </div>
                )}
              </Form.Item>
            ) : (
              <Form.Item
                label="External Link URL"
                help="Enter the URL where the Abdominal US can be accessed"
              >
                <Input
                  value={externalLink}
                  onChange={(e) => setExternalLink(e.target.value)}
                  placeholder="https://example.com/abdominal-us-link"
                />
              </Form.Item>
            )}
          </Col>

          {/* Right Column - Comprehensive Abdominal US Parameters */}
          <Col xs={24} lg={16}>
            <h4>{isVietnamese ? 'Kết quả Siêu âm Bụng Toàn diện' : 'Comprehensive Abdominal US Interpretation'}</h4>

            {/* Organ System Parameters - Collapsible */}
            <Collapse defaultActiveKey={['liver']} ghost>
              {Object.entries(organGroups).map(([organName, parameters]) => (
                parameters.length > 0 && (
                  <Panel
                    header={`${organName} (${parameters.length} ${isVietnamese ? 'tham số' : 'parameters'})`}
                    key={organName.toLowerCase()}
                  >
                    <Row gutter={[16, 16]}>
                      {parameters.map(param => (
                        <Col xs={24} sm={12} lg={8} key={param.key}>
                          <Form.Item
                            name={param.key}
                            label={getTranslatedParameterName(param.key)}
                            rules={[{ required: false }]}
                          >
                            {renderParameterInput(param)}
                          </Form.Item>
                        </Col>
                      ))}
                    </Row>
                  </Panel>
                )
              ))}
            </Collapse>
          </Col>
        </Row>

        <Divider />

        {/* Action Buttons */}
        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
          <Space>
            <Button onClick={onCancel}>
              {isVietnamese ? 'Hủy' : 'Cancel'}
            </Button>
            <Button type="primary" htmlType="submit">
              {isVietnamese ? 'Lưu dữ liệu Siêu âm Bụng' : 'Save Abdominal US Data'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AbdominalUSStudyInputForm;
